import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currencyCtx";
import { fmt, formatPct } from "@/lib/currency";
import { Link } from "react-router-dom";
import { Users, FileText, Settings, ChevronRight, Crown } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const CHART = ["#0A2A24", "#19433A", "#2C5C50", "#C5A059", "#8E6F32"];

export default function AdminOverview() {
  const { currency, rate } = useCurrency();
  const [partners, setPartners] = useState([]);
  const [dists, setDists] = useState([]);
  const [config, setConfig] = useState(null);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [pRes, dRes, cRes] = await Promise.all([
        api.get("/partners"),
        api.get("/distributions"),
        api.get("/config"),
      ]);
      setPartners(pRes.data);
      setDists(dRes.data);
      setConfig(cRes.data);
      const latestPub = dRes.data.find((d) => d.status === "published");
      if (latestPub) {
        const r = await api.get(`/distributions/${latestPub.distribution_id}`);
        setLatest(r.data);
      }
      setLoading(false);
    })();
  }, []);

  const founderCount = useMemo(() => partners.filter((p) => p.is_founder).length, [partners]);

  const byClass = useMemo(() => {
    const m = {};
    partners.forEach((p) => { const k = p.position || "Equity Partner"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  }, [partners]);

  const byLeadership = useMemo(() => {
    const m = {};
    partners.forEach((p) => { if (p.leadership_role) m[p.leadership_role] = (m[p.leadership_role] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  }, [partners]);

  return (
    <div className="anim-stagger">
      <div className="mb-10">
        <div className="eyebrow mb-3">Administrator Console</div>
        <h1 className="display-serif text-5xl sm:text-6xl text-atf-brand">
          The Partnership, <span className="italic">at a glance.</span>
        </h1>
        <p className="text-atf-text2 mt-4 max-w-2xl">
          Maintain the partner roster, configure phase-wise weights, and orchestrate annual
          distributions. Every figure rendered here is what your partners will see.
        </p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 luxe-card mb-10">
        <Stat label="Active Phase" value={config?.phase_name || "—"} sub={`Growth reserve ${config?.growth_reserve_pct ?? 20}%`} />
        <Stat label="Partners" value={partners.length} sub={`${founderCount} founder${founderCount === 1 ? "" : "s"}`} testid="stat-partners" />
        <Stat label="Distributions" value={dists.length} sub={`${dists.filter((d) => d.status === "published").length} published`} testid="stat-distributions" />
        <Stat
          label="Latest Pool"
          value={latest ? fmt(latest.distributable_pool, currency, rate) : "—"}
          sub={latest ? `FY ${latest.year}` : "No published year"}
          testid="stat-pool"
          highlight
        />
      </div>

      {/* Partnership composition */}
      {partners.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="luxe-card p-7">
            <div className="eyebrow">Partnership tiers</div>
            <h3 className="font-serif text-2xl text-atf-brand mt-1">By class</h3>
            <div className="gold-rule-short mt-3" />
            <div className="mt-4" style={{ height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={byClass} dataKey="value" innerRadius={55} outerRadius={85} paddingAngle={3} stroke="#FFFCF5" strokeWidth={2}>
                    {byClass.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#FFFCF5", border: "1px solid #D9C99E", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5">
              {byClass.map((c, i) => (
                <div key={c.name} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5" style={{ background: CHART[i % CHART.length] }} />{c.name}</span>
                  <span className="tabular">{c.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="luxe-card p-7">
            <div className="eyebrow">Leadership</div>
            <h3 className="font-serif text-2xl text-atf-brand mt-1">By role</h3>
            <div className="gold-rule-short mt-3" />
            {byLeadership.length === 0 ? (
              <div className="text-sm text-atf-text2 mt-6">No leadership roles assigned.</div>
            ) : (
              <div className="mt-4 space-y-3">
                {byLeadership.map((l) => {
                  const total = byLeadership.reduce((a, b) => a + b.value, 0);
                  const pct = (l.value / total) * 100;
                  return (
                    <div key={l.name}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span>{l.name}</span>
                        <span className="tabular text-atf-text2">{l.value}</span>
                      </div>
                      <div className="gauge"><span style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="luxe-card-dark p-7">
            <div className="eyebrow !text-atf-gold">The Partnership</div>
            <div className="font-serif text-6xl text-white mt-2 leading-none">{partners.length}</div>
            <div className="gold-rule-short mt-4" />
            <div className="mt-4 text-sm text-white/70">Total equity-bearing partners.</div>
            <div className="mt-6 grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
              <div>
                <div className="eyebrow !text-atf-gold/70">Founders</div>
                <div className="font-serif text-3xl text-atf-gold mt-1.5">{founderCount}</div>
              </div>
              <div>
                <div className="eyebrow !text-atf-gold/70">In leadership</div>
                <div className="font-serif text-3xl text-white mt-1.5">{byLeadership.reduce((a, b) => a + b.value, 0)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card
          to="/admin/partners"
          icon={Users}
          eyebrow="Roster"
          title="Manage Partners"
          desc="Add, update, or retire partners. Capture role, tenure, capital, and founder status."
          count={partners.length}
        />
        <Card
          to="/admin/distributions"
          icon={FileText}
          eyebrow="Annual"
          title="Run Distributions"
          desc="Enter net profit, capture per-partner revenue & capital, simulate and publish."
          count={dists.length}
        />
        <Card
          to="/admin/config"
          icon={Settings}
          eyebrow="Engine"
          title="Configuration"
          desc="Phase weights, tenure bands, leadership points, FX rate — all in one place."
          count={null}
        />
      </div>

      {latest && (
        <div className="mt-2 luxe-card p-8">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="eyebrow mb-1">Latest Published Distribution</div>
              <h2 className="font-serif text-3xl text-atf-brand">{latest.title} · FY {latest.year}</h2>
              <div className="gold-rule-short mt-3" />
            </div>
            <Link
              to={`/admin/distributions/${latest.distribution_id}`}
              data-testid="latest-dist-link"
              className="text-xs uppercase tracking-[0.18em] font-semibold text-atf-brand border-b border-atf-gold pb-1 hover:text-atf-gold3"
            >
              Inspect →
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6">
            <Mini label="Net Profit" value={fmt(latest.net_profit, currency, rate)} />
            <Mini label="Growth Reserve" value={fmt(latest.growth_reserve_amount, currency, rate)} sub={`${latest.growth_reserve_pct}%`} />
            <Mini label="Distributable" value={fmt(latest.distributable_pool, currency, rate)} highlight />
            <Mini label="Partners Paid" value={latest.rows.length} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, testid, highlight }) {
  return (
    <div className={`p-6 border-r border-atf-border-soft last:border-r-0 ${highlight ? "luxe-card-dark !border-0" : ""}`} data-testid={testid}>
      <div className={`eyebrow ${highlight ? "!text-atf-gold" : ""}`}>{label}</div>
      <div className={`mt-3 font-serif text-3xl ${highlight ? "text-atf-gold" : "text-atf-brand"}`}>{value}</div>
      <div className={`mt-1 text-xs ${highlight ? "text-white/60" : "text-atf-text2"}`}>{sub}</div>
    </div>
  );
}

function Card({ to, icon: Icon, eyebrow, title, desc, count }) {
  return (
    <Link
      to={to}
      data-testid={`admin-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className="group luxe-card p-7 hover:border-atf-gold transition-colors flex flex-col"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="w-10 h-10 flex items-center justify-center bg-atf-brand text-atf-gold">
          <Icon size={16} />
        </div>
        {count !== null && <div className="font-mono text-sm text-atf-text2">{count}</div>}
      </div>
      <div className="eyebrow mb-2">{eyebrow}</div>
      <h3 className="font-serif text-2xl text-atf-brand mb-3">{title}</h3>
      <p className="text-sm text-atf-text2 leading-relaxed flex-1">{desc}</p>
      <div className="mt-5 text-xs uppercase tracking-[0.18em] font-semibold text-atf-brand flex items-center gap-1 group-hover:text-atf-gold3 group-hover:gap-2 transition-all">
        Open <ChevronRight size={14} />
      </div>
    </Link>
  );
}

function Mini({ label, value, sub, highlight }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className={`mt-2 font-serif text-2xl ${highlight ? "text-atf-gold3" : "text-atf-brand"}`}>{value}</div>
      {sub && <div className="text-xs text-atf-text2 mt-1">{sub}</div>}
    </div>
  );
}
